/**
 * Core types for building JSON-Schema types
 * - See /types folder
 */
import { AJV_EXTENSION_PROPERTY_PATH } from '../../../../globals';
import { ValidJSONSchemaFields } from './json-schema-fields';

export type IsNever<K extends never> = K;
export type InvalidTypeFields<K> = Exclude<
    keyof Required<K>,
    ValidJSONSchemaFields | '__ts_type' | 'x-property-path' | 'x-object-type'
>;

/**
 * Schema type foundation
 */
export enum SchemaTypeName {
    'array' = 'array',
    'boolean' = 'boolean',
    'integer' = 'integer',
    'null' = 'null',
    'number' = 'number',
    'object' = 'object',
    'string' = 'string',
}

interface ISchemaTypeInput<TSType> {
    default?: TSType;
    const?: Readonly<TSType>;
    enum?: Readonly<Array<TSType>>;
}

export interface ISchemaTypeFoundation<
    TSType,
    Input extends ISchemaTypeInput<TSType> = ISchemaTypeInput<TSType>
> extends ISchemaTypeInput<TSType> {
    //  Metadata: Top-level schemata only; not needed on nested objects
    $schema?: 'https://json-schema.org/draft/2020-12/schema';
    $id?: string;
    title?: string;

    //  Metadata: Universal requirement
    /** @todo: Is it possible to remove the requirement on `description` within `.anyOf()`, `.oneOf()`, or `array.items`? */
    description: string;
    $comment?: string; // Developer comment

    //  Optional conditions
    const?: Input['const'];
    default?: Input['default'];
    enum?: Input['enum'];

    //  Extension: __ts_type holds Typescript type for schema type
    //  - e.g. `string` for the string type
    //  - Used for infer
    __ts_type?: TSType;

    //  Extension: Add path to property, for refs
    [AJV_EXTENSION_PROPERTY_PATH]?: string;
}

//  Validate that all `ISchemaTypeFoundation` fields are in `ValidJSONSchemaFields`
//  - If there is an error here, type uses an invalid field name
//  - NB: DO NOT DELETE
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type ValidateISchemaTypeFoundationFields = IsNever<InvalidTypeFields<ISchemaTypeFoundation<any>>>;

/**
 * Foundation
 * - Used by "array", "tuple"
 */
export interface ISchemaArrayFoundation {
    type: SchemaTypeName.array;
    minItems?: number;
    maxItems?: number;
    uniqueItems?: boolean;

    // NB: Despite this seeming redundant (`description:string` is on ISchemaTypeFoundation), it is needed to confer
    //    `description` as a default property onto the `ISchemaType` union type
    description: string;
}
